import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useCallback, useEffect, useRef, useState } from 'react';

export const useStickyDesktopSidebar = () => {
  const isMobile = useIsMobile();

  const sidebarRef = useRef<HTMLDivElement>(null);

  const [showAbbreviatedSidebar, setShowAbbreviatedSidebar] = useState(false);
  const [previousScrollPos, setPreviousScrollPos] = useState(0);
  const [expandedSidebarHeight, setExpandedSidebarHeight] = useState<
    null | number
  >(null);

  const [isAnimating, setIsAnimating] = useState(false);
  const [hasAnimatedScrollUp, setHasAnimatedScrollUp] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const handleOnScroll = useCallback(() => {
    if (isMobile) {
      return;
    }

    const isScrollingUp = window.scrollY < previousScrollPos;

    const currentScrollPosition = window.scrollY;

    const sidebarCanFullyFit =
      currentScrollPosition <= (expandedSidebarHeight ?? 900) + 16;

    if (isScrollingUp) {
      if (
        sidebarCanFullyFit &&
        !isAnimating &&
        !hasAnimatedScrollUp &&
        showAbbreviatedSidebar
      ) {
        setFadeOut(true);
        setIsAnimating(true);
        setHasAnimatedScrollUp(true);

        setTimeout(() => {
          setShowAbbreviatedSidebar(false);
          setIsAnimating(false);
          setFadeOut(false);
          setFadeIn(true);
        }, 200);

        setTimeout(() => {
          setFadeIn(false);
        }, 500);
      }
    } else if (!sidebarCanFullyFit) {
      setShowAbbreviatedSidebar(true);
      setHasAnimatedScrollUp(false);
      setFadeIn(true);
    }

    if (expandedSidebarHeight === null) {
      setExpandedSidebarHeight(sidebarRef.current?.clientHeight ?? 900);
    }

    setPreviousScrollPos(window.scrollY);
  }, [
    expandedSidebarHeight,
    hasAnimatedScrollUp,
    isAnimating,
    isMobile,
    previousScrollPos,
    showAbbreviatedSidebar,
  ]);

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);

    return () => {
      window.removeEventListener('scroll', handleOnScroll);
    };
  }, [handleOnScroll]);

  return {
    fadeIn,
    fadeOut,
    showAbbreviatedSidebar,
    sidebarRef,
  };
};
